import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../config";
import { coordinates } from "@/interface/dataInterface";
import { format } from "date-fns";
import axiosInstance from "@/utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Copy, Share } from "lucide-react";
import { toast } from "sonner";
import ShareVehicleLocation from "./ShareVehicleLocation";

interface Props extends coordinates {
  startTime?: string;
  endTime?: string;
}

const AddressComponent: React.FC<Props> = ({
  pickup_latitude,
  pickup_longitude,
  dropoff_latitude,
  dropoff_longitude,
  startTime,
  endTime,
}) => {
  const [address, setAddress] = useState<string | null>(null);
  const [dropoffAddress, setDropoffAddress] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const previousCoords = React.useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);

  useEffect(() => {
    if (
      previousCoords.current &&
      previousCoords.current.latitude === pickup_latitude &&
      previousCoords.current.longitude === pickup_longitude
    ) {
      return;
    }

    previousCoords.current = {
      latitude: pickup_latitude,
      longitude: pickup_longitude,
    };

    const fetchAddress = async () => {
      try {
        setLoading(true);
        const url = `/maps/reverse-geocoding?lat=${pickup_latitude}&lng=${pickup_longitude}`;
        const { data } = await axiosInstance.get(url);

        setAddress(data.full_address || "Address not found");
      } catch (error) {
        setError("Address not found");
      } finally {
        setLoading(false);
      }
    };

    fetchAddress();
  }, [pickup_latitude, pickup_longitude]);

  useEffect(() => {
    if (!dropoff_latitude && !dropoff_longitude) return;

    const fetchAddress = async () => {
      try {
        setLoading(true);
        const url = `/maps/reverse-geocoding?lat=${dropoff_latitude}&lng=${dropoff_longitude}`;
        const { data } = await axiosInstance.get(url);

        setDropoffAddress(data.full_address || "Address not found");
      } catch (error) {
        setError("Address not found");
      } finally {
        setLoading(false);
      }
    };

    fetchAddress();
  }, [dropoff_latitude, dropoff_longitude]);

  return (
    <div>
      {loading ? (
        <p>Search address ...</p>
      ) : error ? (
        <p>{error}</p>
      ) : (
        <>
          <div className="flex items-start">
            <div className="text-[12px] flex flex-col">
              <div className="flex items-center gap-x-2">
                {address}
                <Button
                  className="w-6 h-6 p-1"
                  variant="outline"
                  onClick={() => 
                  {
                    navigator.clipboard.writeText(
                      `https://www.google.com/maps?q=${encodeURIComponent(address)}@${pickup_latitude},${pickup_longitude}`,
                    )
                    toast("Location copied")
                  }
                  }
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <span className="text-[10px] text-gray-400">
                {startTime && format(new Date(startTime), "HH:mm")}
              </span>
            </div>
          </div>
          <div>
            <p className="text-[12px] flex flex-col">
              {dropoffAddress}
              <span className="text-[10px] text-gray-400">
                {endTime && format(new Date(endTime), "HH:mm")}
              </span>
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const Address = React.memo(
  AddressComponent,
  (prevProps, nextProps) =>
    prevProps.pickup_latitude === nextProps.pickup_latitude &&
    prevProps.pickup_longitude === nextProps.pickup_longitude,
);

Address.displayName = "Address";

export default Address;
